import { getUnitRequest } from '@/api';
import Button from '@/components/shared/Button';
import Card from '@/components/shared/Card';
import PropertyOverviewCard from '@/components/shared/PropertyOverviewCard';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { profileSelector } from '@/store/profile/profileSlices';
import { IProperty, PaymentMethod, PaymentPeriod, IUnit } from '@/types';
import getAxiosErrors from '@/utils/getAxiosErrors';
import './TenantUnitDetailsPage.scss';

const baseClass = 'TenantUnitDetailsPage';

function TenantUnitDetailsPage() {
  const { unitId } = useParams<{
    unitId: string;
  }>();
  const navigate = useNavigate();
  const [property, setProperty] = useState<IProperty | null>(null);
  const [isPropertyLoading, setIsPropertyLoading] = useState(false);
  const profile = useSelector(profileSelector);
  const numericUnitId = Number(unitId);

  const unit = property?.units?.find(u => u.id === numericUnitId);

  useEffect(() => {
    if (!unitId) return;

    setIsPropertyLoading(true);
    getUnitRequest(property?.id || 0, numericUnitId)
      .then((res) => {
        setProperty(res.data);
      })
      .catch((err) => {
        const errs = getAxiosErrors(err);
        console.debug(errs);
      })
      .finally(() => {
        setIsPropertyLoading(false);
      });
  }, [unitId, numericUnitId, property?.id]);

  if (isPropertyLoading) {
    return (
      <Card className={baseClass}>
        <h4>Loading...</h4>
      </Card>
    );
  }

  return (
    <div className={baseClass}>
      <Card.Header className={`${baseClass}--header`}>
        <span className={`${baseClass}--header-text`}>
          <span className={`${baseClass}--header-text-prop`}>
            My Unit: {unit?.number}
          </span>
        </span>
        <div className={`${baseClass}--header-btns`}>
          <Button
            className={`${baseClass}--header-btn`}
            theme={'secondary'}
            onClick={() => {
              navigate(`/rented`);
            }}
          >
            Back to properties
          </Button>
        </div>
      </Card.Header>

      <PropertyOverviewCard
        property={property}
        navButton={<span />}
      />

      <Card className={`${baseClass}--information mb-24`}>
        <Card.Header className={`${baseClass}--information-header`}>
          <Card.Title className={`${baseClass}--information-title`}>
            <span>Unit information</span>
            {unit?.type === 'residential' && (
              <div className={`${baseClass}--information-type residential`}>
                residential
              </div>
            )}
            {unit?.type === 'commercial' && (
              <div className={`${baseClass}--information-type commercial`}>
                commercial
              </div>
            )}
          </Card.Title>
        </Card.Header>
        <Card.Body className={`${baseClass}--information-body`}>
          <Card.Container className={`${baseClass}--information-container`}>
            <div className={`${baseClass}--information-top`}>
              <div className={`${baseClass}--information-top-column`}>
                <div className={`${baseClass}--information-top-row`}>
                  <div className={`${baseClass}--information-top-row-label`}>
                    Floor of the building
                  </div>
                  <div className={`${baseClass}--information-top-row-value`}>
                    {unit?.floor}
                  </div>
                </div>
                <div className={`${baseClass}--information-top-row`}>
                  <div className={`${baseClass}--information-top-row-label`}>
                    Dimension
                  </div>
                  <div className={`${baseClass}--information-top-row-value`}>
                    {unit?.dimension}
                  </div>
                </div>
                <div className={`${baseClass}--information-top-row`}>
                  <div className={`${baseClass}--information-top-row-label`}>
                    Number of floors
                  </div>
                  <div className={`${baseClass}--information-top-row-value`}>
                    {unit?.numberOfFloors}
                  </div>
                </div>
              </div>
              <div className={`${baseClass}--information-top-column`}>
                <div className={`${baseClass}--information-top-row`}>
                  <div className={`${baseClass}--information-top-row-label`}>
                    Rooms
                  </div>
                  <div className={`${baseClass}--information-top-row-value`}>
                    {unit?.roomsCount}
                  </div>
                </div>
                <div className={`${baseClass}--information-top-row`}>
                  <div className={`${baseClass}--information-top-row-label`}>
                    Bedrooms
                  </div>
                  <div className={`${baseClass}--information-top-row-value`}>
                    {unit?.bedrooms}
                  </div>
                </div>
                <div className={`${baseClass}--information-top-row`}>
                  <div className={`${baseClass}--information-top-row-label`}>
                    Bathrooms
                  </div>
                  <div className={`${baseClass}--information-top-row-value`}>
                    {unit?.bathrooms}
                  </div>
                </div>
              </div>
            </div>

            <div className={`${baseClass}--information-table`}>
              <div className={`${baseClass}--information-table-th`}>
                <div className={`${baseClass}--information-table-th-item`}>
                  ROOM
                </div>
                <div className={`${baseClass}--information-table-th-item`}>
                  FLOOR
                </div>
                <div className={`${baseClass}--information-table-th-item`}>
                  DIMENSION
                </div>
                <div className={`${baseClass}--information-table-th-item`}>
                  FLOORING
                </div>
              </div>

              {(unit?.rooms || []).map(
                (
                  room: {
                    type?: string | null;
                    floor?: number | null;
                    flooring?: string | null;
                    dimension?: number | null;
                  },
                  index: number,
                ) => (
                  <div
                    key={index}
                    className={`${baseClass}--information-table-tr`}
                  >
                    <div className={`${baseClass}--information-table-cell`}>
                      {room.type}
                    </div>
                    <div className={`${baseClass}--information-table-cell`}>
                      {room.floor}
                    </div>
                    <div className={`${baseClass}--information-table-cell`}>
                      {room.dimension}
                    </div>
                    <div className={`${baseClass}--information-table-cell`}>
                      {room.flooring}
                    </div>
                  </div>
                ),
              )}
            </div>
          </Card.Container>
        </Card.Body>
      </Card>

      <Card className={`${baseClass}--inclusions mb-24`}>
        <Card.Header className={`${baseClass}--inclusions-header`}>
          <Card.Title className={`${baseClass}--inclusions-title`}>
            What's included
          </Card.Title>
          <Card.TitleDescription
            className={`${baseClass}--inclusions-title-description`}
          >
            Your rental includes the following items and amenities.
          </Card.TitleDescription>
        </Card.Header>
        <Card.Body className={`${baseClass}--inclusions-body`}>
          <Card.Container className={`${baseClass}--inclusions-container`}>
            <div className={`${baseClass}--inclusions-section`}>
              <div className={`${baseClass}--inclusions-label`}>Parking</div>
              <div className={`${baseClass}--inclusions-items`}>
                {unit?.characteristics.parking.hasOutdoor && (
                  <div className={`${baseClass}--inclusions-item`}>
                    <i className={`fa-solid fa-check ${baseClass}--inclusions-checked`}></i>
                    <span>Outdoor parking</span>
                    {unit?.characteristics.parking.outdoorCount && (
                      <span className={`${baseClass}--inclusions-detail`}>
                        ({unit?.characteristics.parking.outdoorCount} space{unit?.characteristics.parking.outdoorCount !== 1 ? 's' : ''})
                      </span>
                    )}
                  </div>
                )}
                {unit?.characteristics.parking.hasIndoor && (
                  <div className={`${baseClass}--inclusions-item`}>
                    <i className={`fa-solid fa-check ${baseClass}--inclusions-checked`}></i>
                    <span>Indoor parking</span>
                    {unit?.characteristics.parking.indoorCount && (
                      <span className={`${baseClass}--inclusions-detail`}>
                        ({unit?.characteristics.parking.indoorCount} space{unit?.characteristics.parking.indoorCount !== 1 ? 's' : ''})
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className={`${baseClass}--inclusions-section`}>
              <div className={`${baseClass}--inclusions-label`}>Storage</div>
              <div className={`${baseClass}--inclusions-items`}>
                {unit?.characteristics.storage.hasLockerOrStorage && (
                  <div className={`${baseClass}--inclusions-item`}>
                    <i className={`fa-solid fa-check ${baseClass}--inclusions-checked`}></i>
                    <span>Storage space</span>
                    {unit?.characteristics.storage.storageDescription && (
                      <span className={`${baseClass}--inclusions-detail`}>
                        ({unit?.characteristics.storage.storageDescription})
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className={`${baseClass}--inclusions-section`}>
              <div className={`${baseClass}--inclusions-label`}>Appliances</div>
              <div className={`${baseClass}--inclusions-grid`}>
                {unit?.characteristics.furniture.appliances.stove && <div className={`${baseClass}--inclusions-item`}><i className="fa-solid fa-check"></i> Stove</div>}
                {unit?.characteristics.furniture.appliances.refrigerator && <div className={`${baseClass}--inclusions-item`}><i className="fa-solid fa-check"></i> Refrigerator</div>}
                {unit?.characteristics.furniture.appliances.dishwasher && <div className={`${baseClass}--inclusions-item`}><i className="fa-solid fa-check"></i> Dishwasher</div>}
                {unit?.characteristics.furniture.appliances.washer && <div className={`${baseClass}--inclusions-item`}><i className="fa-solid fa-check"></i> Washer</div>}
                {unit?.characteristics.furniture.appliances.dryer && <div className={`${baseClass}--inclusions-item`}><i className="fa-solid fa-check"></i> Dryer</div>}
                {unit?.characteristics.furniture.appliances.microwave && <div className={`${baseClass}--inclusions-item`}><i className="fa-solid fa-check"></i> Microwave</div>}
              </div>
            </div>

            {unit?.characteristics.furniture.isIncluded && (
              <div className={`${baseClass}--inclusions-section`}>
                <div className={`${baseClass}--inclusions-label`}>Furniture</div>
                <div className={`${baseClass}--inclusions-note`}>
                  <i className={`fa-solid fa-check ${baseClass}--inclusions-checked`}></i>
                  <span>Furniture is included in your rent</span>
                </div>
              </div>
            )}
          </Card.Container>
        </Card.Body>
      </Card>

      <Card className={`${baseClass}--lease mb-24`}>
        <Card.Header className={`${baseClass}--lease-header`}>
          <Card.Title className={`${baseClass}--lease-title`}>
            Your lease terms
          </Card.Title>
        </Card.Header>
        <Card.Body className={`${baseClass}--lease-body`}>
          <Card.Container className={`${baseClass}--lease-container`}>
            {unit?.lease?.isFixed === 'fixed' ? (
              <div className={`${baseClass}--lease-content`}>
                <div className={`${baseClass}--lease-content-title`}>
                  <i className="fa-solid fa-calendar-days"></i>
                  <span>Fixed term lease</span>
                </div>
                <div className={`${baseClass}--lease-content-row`}>
                  <span>Lease period: </span>
                  <span className={`${baseClass}--lease-tag`}>
                    {unit?.lease?.period}
                  </span>
                </div>
                <div className={`${baseClass}--lease-content-row`}>
                  <span>From: </span>
                  <span className={`${baseClass}--lease-tag`}>
                    {unit?.lease?.startDate
                      ? moment(unit.lease.startDate).format('MMMM DD, YYYY')
                      : 'N/A'}
                  </span>
                </div>
                <div className={`${baseClass}--lease-content-row`}>
                  <span>To: </span>
                  <span className={`${baseClass}--lease-tag`}>
                    {unit?.lease?.endDate
                      ? moment(unit.lease.endDate).format('MMMM DD, YYYY')
                      : 'N/A'}
                  </span>
                </div>
              </div>
            ) : (
              <div className={`${baseClass}--lease-content`}>
                <div className={`${baseClass}--lease-content-title`}>
                  <i className="fa-solid fa-calendar-days"></i>
                  <span>Month-to-month lease</span>
                </div>
                <div className={`${baseClass}--lease-content-row`}>
                  <span>Started: </span>
                  <span className={`${baseClass}--lease-tag`}>
                    {unit?.lease?.indeterminateStartDate
                      ? moment(unit.lease.indeterminateStartDate).format('MMMM DD, YYYY')
                      : 'N/A'}
                  </span>
                </div>
              </div>
            )}
          </Card.Container>
        </Card.Body>
      </Card>

      <Card className={`${baseClass}--rent mb-24`}>
        <Card.Header className={`${baseClass}--rent-header`}>
          <Card.Title className={`${baseClass}--rent-title`}>
            Rent & Payment Information
          </Card.Title>
        </Card.Header>
        <Card.Body className={`${baseClass}--rent-body`}>
          <Card.Container className={`${baseClass}--rent-container`}>
            <div className={`${baseClass}--rent-breakdown`}>
              <div className={`${baseClass}--rent-item`}>
                <div className={`${baseClass}--rent-item-label`}>
                  <i className="fa-solid fa-home"></i>
                  <span>Base rent</span>
                </div>
                <div className={`${baseClass}--rent-item-value`}>
                  <span className={`${baseClass}--rent-amount`}>
                    ${unit?.rentPrice && unit?.rentPrice.toFixed(2)}
                  </span>
                  <span className={`${baseClass}--rent-period`}>
                    per {unit?.rentPricePeriod === PaymentPeriod.MONTH ? 'month' : 'week'}
                  </span>
                </div>
              </div>

              <div className={`${baseClass}--rent-item`}>
                <div className={`${baseClass}--rent-item-label`}>
                  <i className="fa-solid fa-wrench"></i>
                  <span>Services</span>
                </div>
                <div className={`${baseClass}--rent-item-value`}>
                  <span className={`${baseClass}--rent-amount`}>
                    ${unit?.costOfServices && unit?.costOfServices.toFixed(2)}
                  </span>
                  <span className={`${baseClass}--rent-period`}>
                    per {unit?.costOfServicesPeriod === PaymentPeriod.MONTH ? 'month' : 'week'}
                  </span>
                </div>
              </div>

              <div className={`${baseClass}--rent-item ${baseClass}--rent-total`}>
                <div className={`${baseClass}--rent-item-label`}>
                  <i className="fa-solid fa-calculator"></i>
                  <span>Total monthly rent</span>
                </div>
                <div className={`${baseClass}--rent-item-value`}>
                  <span className={`${baseClass}--rent-amount`}>
                    ${unit?.totalRentPrice && unit?.totalRentPrice.toFixed(2)}
                  </span>
                  <span className={`${baseClass}--rent-period`}>
                    per {unit?.totalRentPricePeriod === PaymentPeriod.MONTH ? 'month' : 'week'}
                  </span>
                </div>
              </div>
            </div>

            <div className={`${baseClass}--payment-info`}>
              <div className={`${baseClass}--payment-section`}>
                <div className={`${baseClass}--payment-title`}>
                  <i className="fa-solid fa-calendar-check"></i>
                  <span>Payment schedule</span>
                </div>
                <div className={`${baseClass}--payment-detail`}>
                  <span>Rent is due on the </span>
                  <span className={`${baseClass}--lease-tag`}>
                    {unit?.paymentFirstDay === PaymentPeriod.MONTH
                      ? '1st day of each month'
                      : '1st day of each week'}
                  </span>
                </div>
                {unit?.firstPaymentDate && (
                  <div className={`${baseClass}--payment-detail`}>
                    <span>Next payment due: </span>
                    <span className={`${baseClass}--lease-tag`}>
                      {moment(unit.firstPaymentDate).format('MMMM DD, YYYY')}
                    </span>
                  </div>
                )}
              </div>

              <div className={`${baseClass}--payment-section`}>
                <div className={`${baseClass}--payment-title`}>
                  <i className="fa-solid fa-credit-card"></i>
                  <span>Payment method</span>
                </div>
                <div className={`${baseClass}--payment-detail`}>
                  <span>
                    {unit?.paymentMethod === PaymentMethod.CASH && 'Cash'}
                    {unit?.paymentMethod === PaymentMethod.CHEQUE && 'Cheque'}
                    {unit?.paymentMethod === PaymentMethod.ELECTRONIC_TRANSFER && 'Electronic transfer'}
                    {unit?.paymentMethod === PaymentMethod.OTHER &&
                      (unit?.paymentMethodOtherSpecify || 'Other method')}
                  </span>
                </div>
              </div>
            </div>
          </Card.Container>
        </Card.Body>
      </Card>

      <Card className={`${baseClass}--contact mb-24`}>
        <Card.Header className={`${baseClass}--contact-header`}>
          <Card.Title className={`${baseClass}--contact-title`}>
            Property manager contact
          </Card.Title>
        </Card.Header>
        <Card.Body className={`${baseClass}--contact-body`}>
          <Card.Container className={`${baseClass}--contact-container`}>
            <div className={`${baseClass}--contact-info`}>
              <div className={`${baseClass}--contact-item`}>
                <i className="fa-solid fa-user"></i>
                <span>{property?.owner?.firstname} {property?.owner?.lastname}</span>
              </div>
              <div className={`${baseClass}--contact-item`}>
                <i className="fa-solid fa-envelope"></i>
                <a href={`mailto:${property?.owner?.email}`}>
                  {property?.owner?.email}
                </a>
              </div>
              {property?.owner?.phone && (
                <div className={`${baseClass}--contact-item`}>
                  <i className="fa-solid fa-phone"></i>
                  <a href={`tel:${property?.owner?.phone}`}>
                    {property?.owner?.phone}
                  </a>
                </div>
              )}
            </div>
          </Card.Container>
        </Card.Body>
      </Card>
    </div>
  );
}

export default TenantUnitDetailsPage;